import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StringService {

  constructor() {
    this.abbreviateName = this.abbreviateName.bind(this);
   }

  static normalize(text: string) {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  abbreviateName(fullName: string, opts: {locale: string}) {
    if (!fullName || typeof fullName !== 'string') return '';

    const locale = opts.locale || 'pt-BR';

    // Palavras a remover (preposições/artigos/conjunções) – minúsculas
    const DROP = new Set([
      'de', 'da', 'do', 'das', 'dos', 'd', 'd\'', 'd’', 'e',
      'del', 'della', 'di', 'du', 'des', 'le', 'la', 'les',
      'de la', 'de las', 'de los', 'van', 'von', 'der'
    ]);

    // Sufixos comuns no Brasil (minúsculas)
    const SUFFIXES = new Set(['filho', 'neta', 'neto', 'sobrinho', 'junior', 'júnior', 'jr', 'jr.', 'segundo', 'terceiro', 'ii', 'iii']);

    const toTitle = (w) =>
      w ? w.charAt(0).toLocaleUpperCase(locale) + w.slice(1).toLocaleLowerCase(locale) : w;

    const toTitleHyphen = (w) =>
      w.split('-').map(toTitle).join('-');

    // Normaliza espaços
    let tokens = fullName.replace(/\s+/g, ' ').trim().split(' ');
    if (tokens.length === 0) return '';

    // Remove pontuação periférica simples
    tokens = tokens.map(t => t.replace(/^[."']+|[."']+$/g, ''));

    // Extrai sufixo se existir (ex.: "Filho", "Neto", "Júnior/Jr.")
    let suffix = '';
    if (tokens.length >= 2) {
      const last = tokens[tokens.length - 1].toLocaleLowerCase(locale);
      if (SUFFIXES.has(last)) {
        suffix = tokens.pop(); // remove do final
      }
    }

    if (tokens.length === 0) return toTitle(suffix);

    // Sobrenome final (mantido por extenso)
    let lastSurname = tokens.pop();

    // Tratamento de "d'Ávila" como preposição + núcleo
    const stripDApostrophe = (t) =>
      t.replace(/^(d['’])(.+)$/i, (_, __, rest) => rest);

    lastSurname = stripDApostrophe(lastSurname);

    // Primeiro nome (mantido por extenso)
    let firstName = tokens.length ? tokens.shift() : lastSurname;

    // Se por algum motivo o último caiu para firstName (nome único), ajusta
    if (firstName === lastSurname && tokens.length === 0) {
      return [toTitleHyphen(firstName), suffix ? this.normalSuffix(suffix, locale) : ''].filter(Boolean).join(' ');
    }

    // Processa nomes do meio: remove preps e abrevia
    const halfAbbreviated = tokens
      .map(stripDApostrophe)
      .filter((t, i, arr) => {
        // remove tokens que são preposições/artigos (inclui combos "de la")
        const low = t.toLocaleLowerCase(locale);
        if (DROP.has(low)) return false;
        // Trata combinação "de la", "de los", etc. (olha par atual+seguinte)
        const next = arr[i + 1]?.toLocaleLowerCase(locale);
        const combo = low + ' ' + next;
        if (DROP.has(combo)) {
          arr[i + 1] = ''; // consome o seguinte
          return false;
        }
        return t.trim().length > 0;
      })
      .map(t => {
        // Abrevia token (e cada parte se for hifenizado)
        return t.split('-')
          .filter(Boolean)
          .map(p => (p[0] || '').toLocaleUpperCase(locale) + '.')
          .join('-');
      });

    const parts = [
      toTitleHyphen(firstName),
      ...halfAbbreviated,
      toTitleHyphen(lastSurname)
    ];

    if (suffix) parts.push(this.normalSuffix(suffix, locale));
    return parts.filter(Boolean).join(' ');
  }

  normalSuffix(suf, locale = 'pt-BR') {
    const s = suf.toLocaleLowerCase(locale).replace(/\.$/, '');
    if (s === 'jr') return 'Jr.';            // normaliza "Jr." com ponto
    if (s === 'junior' || s === 'júnior') return 'Júnior';
    if (s === 'ii') return 'II';
    if (s === 'iii') return 'III';
    return this.toTitlePT(suf, locale);
  }

  toTitlePT(w: string, locale = 'pt-BR') {
    return w ? w.charAt(0).toLocaleUpperCase(locale) + w.slice(1).toLocaleLowerCase(locale) : w;
  }

  ellipse(name: string, length: number) {
    return name?.length > length ? name?.substring(0, length - 3) + '...' : name;
  }
}
